import { Suspense, useEffect, useMemo, useState } from 'react';
import { BpmnViewerLazy } from '../bpmn/BpmnViewerLazy';
import { parseBpmnDefinitions } from '../bpmn/model';
import { buildProvenanceIndex } from '../bpmn/provenance';
import type { BpmnDefinitionsModel } from '../bpmn/types';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Tabs } from '../components/Tabs';
import type { Definition, Run } from '../api/types';
import type { ManifestView } from '../microapp/manifest';
import type { DetailPanel } from '../app/router';
import { declaredInputSchema } from '../forms/startInput';
import { ElementDetail } from './ElementDetail';
import { ElementPicker } from './ElementPicker';
import { ProvenancePanel } from './ProvenancePanel';
import { StartRunPanel } from './StartRunPanel';

export interface ProcessTabProps {
  workspaceId: string;
  appId: string;
  definition: Definition;
  manifest: ManifestView;
  /** app.status === 'ready' のときだけプロセスを開始できる。 */
  appReady: boolean;
  processKey: string | null;
  elementId: string | null;
  panel: DetailPanel;
  onSelectProcess(processKey: string): void;
  onSelectElement(elementId: string | null): void;
  onSelectPanel(panel: DetailPanel): void;
  onOpenDecision(decisionKey: string): void;
  onRunStarted(run: Run): void;
}

/**
 * app詳細の起点。複数BPMNを切り替え、要素を選んでTaskの実装と出典を辿る。
 * 出典は同じ要素の別タブとして常に開けるようにし、回答待ちのときだけに限定しない。
 */
export function ProcessTab(props: ProcessTabProps) {
  const { definition, processKey, elementId, panel } = props;
  const processes = definition.processes;
  const current = processes.find((entry) => entry.key === processKey) ?? processes[0] ?? null;
  const [model, setModel] = useState<BpmnDefinitionsModel | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    if (!current) return;
    let active = true;
    setModel(null);
    setParseError(null);
    setRenderError(null);
    parseBpmnDefinitions(current.xml)
      .then((parsed) => {
        if (active) setModel(parsed);
      })
      .catch((error: unknown) => {
        if (active) setParseError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      active = false;
    };
  }, [current]);

  // 索引はmodelごとに閉じているので、別BPMNへ切り替えても要素IDが混線しない。
  const provenance = useMemo(() => (model ? buildProvenanceIndex(model) : null), [model]);
  const process = model?.processes.find((entry) => entry.processKey === current?.key) ?? null;

  if (!current) {
    return <p className="hint">このmicroappにはBPMNがありません。</p>;
  }

  return (
    <div className="process-tab">
      <div className="process-toolbar">
        <label className="field inline">
          <span className="field-label">プロセス</span>
          <select
            className="input"
            value={current.key}
            onChange={(event) => props.onSelectProcess(event.target.value)}
          >
            {processes.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.key}
              </option>
            ))}
          </select>
        </label>
        <span className="hint">
          開始: {current.startEvents.map((event) => `${event.id}(${event.type})`).join(', ') || 'なし'}
        </span>
      </div>

      <div className="diagram-layout">
        <div className="diagram-area">
          {model && !model.hasDiagram ? (
            <div className="no-diagram">
              <p className="notice">
                この定義にはBPMNDI（図の配置情報）がありません。図は描けないため、要素を一覧から選びます。
              </p>
              {process && (
                <ElementPicker
                  process={process}
                  selectedElementId={elementId}
                  onSelect={props.onSelectElement}
                />
              )}
            </div>
          ) : (
            <Suspense fallback={<Spinner label="図を読み込んでいます" />}>
              <BpmnViewerLazy
                xml={current.xml}
                selectedElementId={elementId}
                onSelect={props.onSelectElement}
                onImportError={(error) => setRenderError(error.message)}
              />
            </Suspense>
          )}
          {renderError && <p className="failure">図を描画できませんでした: {renderError}</p>}
        </div>

        <aside className="inspector">
          <Tabs
            label="要素の詳細"
            active={panel === 'answer' ? 'detail' : panel}
            onSelect={(key) => props.onSelectPanel(key as DetailPanel)}
            items={[
              { key: 'detail', label: '詳細' },
              { key: 'source', label: '出典' },
            ]}
          />

          {parseError && <p className="failure">BPMNを解析できませんでした: {parseError}</p>}
          {!model && !parseError && <Spinner label="BPMNを解析しています" />}

          {model && provenance && process && (
            <>
              {panel === 'source' ? (
                <ProvenancePanel
                  index={provenance}
                  elementId={elementId}
                  onSelectElement={props.onSelectElement}
                />
              ) : elementId ? (
                <ElementDetail
                  process={process}
                  elementId={elementId}
                  manifest={props.manifest}
                  onOpenDecision={props.onOpenDecision}
                  onOpenProcess={props.onSelectProcess}
                />
              ) : (
                <p className="hint">図の要素を選ぶと、実装と入出力の対応が表示されます。</p>
              )}

              {model.warnings.length > 0 && (
                <Card title={`定義の警告 ${model.warnings.length}件`}>
                  <ul className="warnings">
                    {model.warnings.map((warning, index) => (
                      <li key={`${warning.code}-${index}`}>
                        <code>{warning.code}</code> {warning.message}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </>
          )}
        </aside>
      </div>

      {/* プロセスごとに開始入力の形が違うので、切り替えたら入力中の値を持ち越さない。 */}
      <StartRunPanel
        key={current.key}
        workspaceId={props.workspaceId}
        appId={props.appId}
        process={current}
        inputSchema={declaredInputSchema(props.manifest, definition.schemas, current.key)}
        disabled={!props.appReady}
        onStarted={props.onRunStarted}
      />
    </div>
  );
}
